import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { label: "Work", href: "#showcase" },
  { label: "Craft", href: "#features" },
  { label: "Process", href: "#manifesto" },
  { label: "Journal", href: "#testimonials" },
  { label: "Contact", href: "#final-cta" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el && window.__lenis) {
      window.__lenis.scrollTo(el, { offset: -40, duration: 1.4 });
    } else if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <motion.header
        data-testid="navbar-container"
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1.1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-[80] flex justify-center transition-all duration-500 ${
          scrolled ? "pt-3" : "pt-5"
        }`}
      >
        <nav
          className={`glass flex items-center gap-8 rounded-full transition-all duration-500 ${
            scrolled ? "px-3 py-2" : "px-5 py-3"
          }`}
          style={{ minWidth: "min(760px, 92vw)" }}
        >
          <button
            onClick={() => scrollTo("#hero")}
            className="shrink-0"
            data-testid="navbar-logo"
          >
            <Logo size={26} />
          </button>

          <div className="hidden lg:flex items-center gap-7">
            {LINKS.map((l) => (
              <button
                key={l.label}
                onClick={() => scrollTo(l.href)}
                className="group relative text-[13px] font-medium text-[#C9CBD3] hover:text-[#F4F4F2] transition-colors"
                data-testid={`navbar-link-${l.label.toLowerCase()}`}
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-accent transition-all duration-500 group-hover:w-full" />
              </button>
            ))}
          </div>

          <div className="ml-auto flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 pr-3 border-r border-white/8 text-[11px] font-mono text-[#8A8D98]">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-accent opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
              </span>
              <span>AVAILABLE · Q1 '26</span>
            </div>

            <button
              onClick={() => scrollTo("#final-cta")}
              className="group hidden md:inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-[13px] font-medium text-[#F4F4F2] transition-all hover:border-accent/50 hover:bg-accent/10"
              data-testid="navbar-cta-button"
            >
              Launch project
              <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>

            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden rounded-full border border-white/10 p-2 text-[#F4F4F2]"
              data-testid="navbar-mobile-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="navbar-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] lg:hidden bg-[#050507]/95 backdrop-blur-xl"
          >
            <div className="flex flex-col items-start justify-center h-full px-8 gap-5">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.label}
                  initial={{ y: 30, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.05 * i + 0.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => scrollTo(l.href)}
                  className="font-display text-5xl font-semibold tracking-tight text-[#F4F4F2] hover:text-accent transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.35, duration: 0.6 }}
                onClick={() => scrollTo("#final-cta")}
                className="btn-primary mt-6"
              >
                Launch project
                <ArrowUpRight size={16} />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
